import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { formatPrice, formatDate, getStatusColor, getStatusText } from '../lib/utils';
import { ShoppingBag, User, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [user, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-12 cyber-grid">
      <div className="max-w-4xl mx-auto px-4">
        {/* Profile Header */}
        <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card mb-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center neon-green"
              style={{ background: 'radial-gradient(circle, hsl(var(--primary)/0.2), hsl(var(--card)))' }}>
              <User className="w-8 h-8 text-primary" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 mb-1 px-2 py-0.5 rounded border border-primary/30 bg-primary/5">
                <span className="w-1 h-1 rounded-full bg-primary animate-pulse" />
                <span className="font-mono text-[9px] text-primary tracking-widest uppercase">IDENTITY_VERIFIED</span>
              </div>
              <h1 className="font-bold text-xl text-white tracking-wider">{user.name}</h1>
              <p className="text-muted-foreground text-sm font-mono">{user.email}</p>
              {user.role !== 'user' && (
                <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded font-mono uppercase tracking-widest border border-primary/40 bg-primary/10 text-primary">
                  {user.role}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Orders */}
        <div className="bg-card/80 backdrop-blur-sm rounded-xl border border-border cyber-card overflow-hidden">
          <div className="p-6 border-b border-border bg-black/30 flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-primary" />
            <h2 className="font-bold text-lg text-white uppercase tracking-wider">
              Riwayat Transmisi
            </h2>
            <span className="ml-auto font-mono text-xs text-muted-foreground">
              {loading ? '...' : `${orders.length} RECORDS`}
            </span>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
              <p className="font-mono text-xs text-muted-foreground mt-3 uppercase tracking-widest">Scanning database...</p>
            </div>
          ) : orders.length > 0 ? (
            <div className="divide-y divide-border">
              {orders.map((order) => (
                <div key={order.id} className="p-4 hover:bg-primary/5 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-bold text-white text-sm">{order.product_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{order.denomination_name}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded font-mono uppercase tracking-wider ${getStatusColor(order.status)}`}>
                      {getStatusText(order.status)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-4 text-muted-foreground font-mono">
                      <span>{order.order_number}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(order.created_at)}
                      </span>
                    </div>
                    <span className="font-mono text-primary font-bold text-glow-green">
                      {formatPrice(order.price)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-10 text-center">
              <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
              <p className="text-white font-bold font-mono mb-2 uppercase tracking-wider">[ NO_RECORDS_FOUND ]</p>
              <p className="text-muted-foreground text-sm font-mono mb-6">
                Belum ada transmisi dalam log database
              </p>
              <Button onClick={() => navigate('/')}
                className="bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest neon-green">
                [ Mulai Top Up ]
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
